import { Box, Stack } from '@mui/material';
import { ReactNode } from 'react';

import {
  MainHeading,
  SecondaryHeading,
} from '@/modules/presentation/PresentationSlide/accessoryComponents';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';

import {
  useBundleWaterfall,
  useClientPresentationDesignerContext,
} from '../ClientPresentationDesigner.context';
import { ClientPresentationV2Configuration } from '../ClientPresentationDesigner.types';
import { BUNDLE_TO_DISPLAY_NAME } from './ClientPresentationDesignerTreeView/ClientPresentationDesignerTreeView.constants';
import {
  EstateWaterfallBadges,
  EstateWaterfallBadgesProps,
} from './slides/estateWaterfall/EstateWaterfallBadges';

export interface ClientPresentationLeftHeadingProps
  extends EstateWaterfallBadgesProps {
  displayName?: string;
  /** Override the default bundle-type subheading (e.g. with a page-specific label). */
  subheading?: string;
}

export function ClientPresentationLeftHeading({
  bundle,
  displayName: externalDisplayName,
  subheading: externalSubheading,
}: ClientPresentationLeftHeadingProps) {
  const waterfall = useBundleWaterfall(bundle.id);

  const displayName =
    externalDisplayName || bundle.displayName || waterfall?.displayName;

  // MainHeading wraps `heading` in its own Typography that handles font,
  // weight, color, and size — so we let `displayName` inherit those
  // styles from MainHeading and just lay out the text + badges here.
  // The text is wrapped in a span (rather than a bare text node) so it
  // counts as a Stack child for the spacing={0.5} gap to the badges.
  const heading: ReactNode = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box component="span">{displayName}</Box>
      <EstateWaterfallBadges bundle={bundle} />
    </Stack>
  );

  // Don't show subheading if it's the same as the heading to avoid redundancy
  const bundleTypeDisplayName =
    externalSubheading ?? BUNDLE_TO_DISPLAY_NAME[bundle.type];
  const subheading: ReactNode =
    displayName === bundleTypeDisplayName ? null : bundleTypeDisplayName;

  return <MainHeading heading={heading} subheading={subheading} />;
}

export function getClientPresentationHeaderLines(
  household: { displayName: string } | null,
  configuration: Pick<
    ClientPresentationV2Configuration,
    'showClientNamesInHeader'
  > | null
) {
  if (!household || !configuration?.showClientNamesInHeader) {
    return null;
  }

  return {
    lineOne: household.displayName,
    lineTwo: `As of ${formatDateToMonDDYYYY(new Date())}`,
  };
}

export function ClientPresentationRightHeading() {
  const { household, contextData } = useClientPresentationDesignerContext();
  const lines = getClientPresentationHeaderLines(
    household,
    contextData.presentationConfiguration
  );

  return lines ? <SecondaryHeading {...lines} /> : null;
}
